import { ExternalLink, AlertTriangle, Copy } from 'lucide-react';
import { docuWareLinkState } from '../../types/docuwareInvoices';
import type { DocuWareLinkFields } from '../../types/docuwareInvoices';

/**
 * The link back to the archived document in DocuWare — three honest states, never a fourth.
 *
 *   verified    the URL template for that cabinet has been click-confirmed. Plain link.
 *   unverified  we can build a URL but the template is still a guess, so the UI says so
 *               rather than presenting a maybe as a certainty.
 *   none        no URL can be built. Then the cabinet's base URL and the copyable document
 *               ID are the most honest thing available: enough to find it by hand.
 *
 * Shared by the invoice table and the order-check table. Extracted rather than copied because
 * the second copy is where the three states quietly become two.
 *
 * The states are decided SERVER-side (`documentUrl` / `documentUrlVerified` come from the
 * company's DocuWareConfig); this component only renders them.
 */
interface Props {
    row: DocuWareLinkFields;
    onCopyDocId: (docId: string) => void;
    /** `icon` for a narrow table cell, `inline` next to a heading. */
    variant?: 'icon' | 'inline';
}

export function DocuWareDocLink({ row, onCopyDocId, variant = 'icon' }: Props) {
    const state = docuWareLinkState(row);
    const docId = row.docId === null || row.docId === undefined ? null : String(row.docId);

    if (state === 'verified') {
        return (
            <a
                href={row.documentUrl!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                title="Open in DocuWare"
            >
                <ExternalLink className="h-4 w-4" />
                {variant === 'inline' && <span className="text-xs">Open in DocuWare</span>}
            </a>
        );
    }

    if (state === 'unverified') {
        return (
            <a
                href={row.documentUrl!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-800"
                title="Link format not yet confirmed"
            >
                <AlertTriangle className="h-4 w-4" />
                <span className="text-[11px]">unconfirmed</span>
            </a>
        );
    }

    return (
        <div className="flex items-center gap-2">
            {row.baseUrl && (
                <a
                    href={row.baseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800"
                >
                    Open DocuWare
                </a>
            )}
            {docId && (
                <button
                    onClick={() => onCopyDocId(docId)}
                    className="inline-flex items-center gap-1 font-mono text-xs text-gray-600 hover:text-gray-900"
                    title="Copy document ID"
                >
                    {docId}
                    <Copy className="h-3 w-3" />
                </button>
            )}
        </div>
    );
}
